import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { projects } from '../../data/constants';
import { getProjectBySlug, slugify } from '../../utils/slugify';
import ReactGA from 'react-ga4';

/* ─── Layout ─── */
const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.bg};
  color: ${({ theme }) => theme.text_primary};
  padding: 0;
`;

const TopBar = styled.nav`
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 48px;
  background: ${({ theme }) => theme.bg}ee;
  backdrop-filter: blur(16px);
  border-bottom: 1px solid rgba(133, 76, 230, 0.1);

  @media (max-width: 768px) {
    padding: 14px 20px;
  }
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.primary};
  text-decoration: none;
  padding: 8px 16px;
  border-radius: 20px;
  border: 1.5px solid ${({ theme }) => theme.primary}40;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ theme }) => theme.primary}15;
    border-color: ${({ theme }) => theme.primary};
    transform: translateX(-2px);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const TopBarTitle = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.text_secondary};
  max-width: 400px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 600px) {
    display: none;
  }
`;

const HeroSection = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
`;

const HeroImage = styled(motion.img)`
  width: 100%;
  max-height: 480px;
  object-fit: cover;
  display: block;
  filter: brightness(0.85);

  @media (max-width: 768px) {
    max-height: 300px;
  }
`;

const HeroOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 60%;
  background: linear-gradient(to top, ${({ theme }) => theme.bg}, transparent);
`;

const Content = styled.div`
  max-width: 860px;
  margin: 0 auto;
  padding: 0 48px 80px;
  position: relative;
  z-index: 2;
  margin-top: -60px;

  @media (max-width: 768px) {
    padding: 0 20px 60px;
    margin-top: -40px;
  }
`;

const Category = styled(motion.span)`
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: ${({ theme }) => theme.primary};
  background: ${({ theme }) => theme.primary}18;
  padding: 6px 14px;
  border-radius: 20px;
  margin-bottom: 16px;
`;

const Title = styled(motion.h1)`
  font-size: 42px;
  font-weight: 800;
  line-height: 1.15;
  color: ${({ theme }) => theme.text_primary};
  margin: 0 0 12px;
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const DateLabel = styled(motion.div)`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.text_secondary};
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 16px;
    height: 16px;
    opacity: 0.6;
  }
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background: linear-gradient(90deg, ${({ theme }) => theme.primary}30, transparent);
  margin: 0 0 32px;
`;

const Description = styled(motion.div)`
  font-size: 16px;
  line-height: 1.85;
  color: ${({ theme }) => theme.text_primary};
  white-space: pre-wrap;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    font-size: 15px;
    line-height: 1.75;
  }
`;

const SectionLabel = styled.h3`
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: ${({ theme }) => theme.text_secondary};
  margin: 0 0 16px;
`;

const TagsGrid = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 40px;
`;

const Tag = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.primary};
  background: ${({ theme }) => theme.primary}14;
  border: 1px solid ${({ theme }) => theme.primary}28;
  padding: 6px 14px;
  border-radius: 20px;
  transition: all 0.25s ease;

  &:hover {
    background: ${({ theme }) => theme.primary}25;
    transform: translateY(-1px);
  }
`;

const LinksRow = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 48px;
`;

const LinkBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 25px;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const PrimaryLink = styled(LinkBtn)`
  background: linear-gradient(225deg, hsla(271, 100%, 50%, 1) 0%, hsla(294, 100%, 50%, 1) 100%);
  color: white;
  border: none;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(133, 76, 230, 0.4);
  }
`;

const SecondaryLink = styled(LinkBtn)`
  background: transparent;
  color: ${({ theme }) => theme.primary};
  border: 1.5px solid ${({ theme }) => theme.primary}50;

  &:hover {
    background: ${({ theme }) => theme.primary}12;
    border-color: ${({ theme }) => theme.primary};
    transform: translateY(-2px);
  }
`;

/* ─── Members ─── */
const MembersSection = styled.div`
  margin-bottom: 40px;
`;

const MemberCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 0;
`;

const MemberAvatar = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid ${({ theme }) => theme.primary}30;
`;

const MemberName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
`;

const MemberLinks = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;

  a {
    color: ${({ theme }) => theme.text_secondary};
    transition: color 0.2s ease;

    &:hover {
      color: ${({ theme }) => theme.primary};
    }

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

/* ─── Related Projects ─── */
const RelatedSection = styled.div`
  margin-top: 60px;
  padding-top: 40px;
  border-top: 1px solid rgba(133, 76, 230, 0.1);
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-top: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const RelatedCard = styled(Link)`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.card};
  border-radius: 12px;
  overflow: hidden;
  text-decoration: none;
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition: all 0.3s ease;

  &:hover {
    border-color: ${({ theme }) => theme.primary};
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(133, 76, 230, 0.15);
  }
`;

const RelatedImage = styled.img`
  width: 100%;
  height: 140px;
  object-fit: cover;
`;

const RelatedInfo = styled.div`
  padding: 14px;
`;

const RelatedTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
  line-height: 1.35;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const RelatedCategory = styled.div`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${({ theme }) => theme.primary};
  margin-top: 8px;
`;

/* ─── 404 ─── */
const NotFound = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80vh;
  gap: 20px;
  text-align: center;
  padding: 40px;
`;

const NotFoundTitle = styled.h2`
  font-size: 32px;
  font-weight: 700;
  color: ${({ theme }) => theme.text_primary};
`;

const NotFoundText = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.text_secondary};
`;

/* ─── Icons ─── */
const ArrowLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const GitHubIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

/* ─── Animation variants ─── */
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1, ease: 'easeOut' },
  }),
};

/* ─── Component ─── */
const ProjectCaseStudy = () => {
  const { slug } = useParams();
  const project = getProjectBySlug(projects, slug);

  // SEO: update document title + meta description
  useEffect(() => {
    if (project) {
      document.title = `${project.title} | Urvansh Shah`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute(
          'content',
          `${project.title} — ${project.description?.split('\n')[0]?.substring(0, 150)}...`
        );
      }
      // Update canonical
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute('href', `https://urvanshshah.netlify.app/projects/${slug}`);
      }
      // GA pageview
      ReactGA.send({
        hitType: 'pageview',
        page: `/projects/${slug}`,
        title: project.title,
      });
    }

    // Scroll to top on mount
    window.scrollTo(0, 0);

    // Cleanup: restore original title on unmount
    return () => {
      document.title = 'Urvansh Shah | Data Science Analyst & Software Developer Portfolio | Phoenix, AZ';
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute(
          'content',
          'Urvansh Shah - Senior Data Science Analyst and Software Developer Engineer. MS in Data Science at Arizona State University.'
        );
      }
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute('href', 'https://urvanshshah.netlify.app/');
      }
    };
  }, [project, slug]);

  if (!project) {
    return (
      <PageWrapper>
        <TopBar>
          <BackButton to="/">
            <ArrowLeftIcon />
            Back to Portfolio
          </BackButton>
        </TopBar>
        <NotFound>
          <NotFoundTitle>Project Not Found</NotFoundTitle>
          <NotFoundText>The project you're looking for doesn't exist or has been moved.</NotFoundText>
          <PrimaryLink as={Link} to="/">← Back to Portfolio</PrimaryLink>
        </NotFound>
      </PageWrapper>
    );
  }

  // Get related projects (same category, exclude current)
  const related = projects
    .filter((p) => p.category === project.category && p.id !== project.id)
    .slice(0, 3);

  return (
    <PageWrapper>
      <TopBar>
        <BackButton to="/">
          <ArrowLeftIcon />
          Back to Portfolio
        </BackButton>
        <TopBarTitle>{project.title}</TopBarTitle>
        <div style={{ width: 120 }} /> {/* spacer for centering */}
      </TopBar>

      {project.image && (
        <HeroSection>
          <HeroImage
            src={project.image}
            alt={project.title}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          />
          <HeroOverlay />
        </HeroSection>
      )}

      <Content>
        {project.category && (
          <Category variants={fadeUp} initial="hidden" animate="visible" custom={0}>
            {project.category}
          </Category>
        )}

        <Title variants={fadeUp} initial="hidden" animate="visible" custom={1}>
          {project.title}
        </Title>

        {project.date && (
          <DateLabel variants={fadeUp} initial="hidden" animate="visible" custom={2}>
            <CalendarIcon />
            {project.date}
          </DateLabel>
        )}

        <Divider />

        <Description variants={fadeUp} initial="hidden" animate="visible" custom={3}>
          {project.description}
        </Description>

        {project.tags && project.tags.length > 0 && (
          <>
            <SectionLabel>Technologies & Topics</SectionLabel>
            <TagsGrid variants={fadeUp} initial="hidden" animate="visible" custom={4}>
              {project.tags.map((tag, i) => (
                <Tag key={i}>{tag}</Tag>
              ))}
            </TagsGrid>
          </>
        )}

        <LinksRow variants={fadeUp} initial="hidden" animate="visible" custom={5}>
          {project.github && project.github !== '' && (
            <PrimaryLink href={project.github} target="_blank" rel="noopener noreferrer">
              <GitHubIcon />
              View on GitHub
            </PrimaryLink>
          )}
          {project.webapp && project.webapp !== '' && (
            <SecondaryLink href={project.webapp} target="_blank" rel="noopener noreferrer">
              <ExternalLinkIcon />
              Live Demo
            </SecondaryLink>
          )}
          {project.paper && project.paper !== '' && (
            <SecondaryLink href={project.paper} target="_blank" rel="noopener noreferrer">
              <ExternalLinkIcon />
              Publication
            </SecondaryLink>
          )}
          {project.youtube && project.youtube !== '' && (
            <SecondaryLink href={project.youtube} target="_blank" rel="noopener noreferrer">
              <ExternalLinkIcon />
              YouTube
            </SecondaryLink>
          )}
        </LinksRow>

        {project.member && project.member.length > 0 && (
          <MembersSection>
            <SectionLabel>Team Members</SectionLabel>
            {project.member.map((m, i) => (
              <MemberCard key={i}>
                <MemberAvatar src={m.img} alt={m.name} />
                <MemberName>{m.name}</MemberName>
                <MemberLinks>
                  {m.github && (
                    <a href={m.github} target="_blank" rel="noopener noreferrer">
                      <GitHubIcon />
                    </a>
                  )}
                  {m.linkedin && (
                    <a href={m.linkedin} target="_blank" rel="noopener noreferrer">
                      <LinkedInIcon />
                    </a>
                  )}
                </MemberLinks>
              </MemberCard>
            ))}
          </MembersSection>
        )}

        {related.length > 0 && (
          <RelatedSection>
            <SectionLabel>Related Projects</SectionLabel>
            <RelatedGrid>
              {related.map((r) => (
                <RelatedCard key={r.id} to={`/projects/${slugify(r.title)}`}>
                  <RelatedImage src={r.image} alt={r.title} loading="lazy" />
                  <RelatedInfo>
                    <RelatedTitle>{r.title}</RelatedTitle>
                    <RelatedCategory>{r.category}</RelatedCategory>
                  </RelatedInfo>
                </RelatedCard>
              ))}
            </RelatedGrid>
          </RelatedSection>
        )}
      </Content>
    </PageWrapper>
  );
};

export default ProjectCaseStudy;
